import React, { useState, useMemo, useCallback, useEffect, useRef, useLayoutEffect } from 'react';
import {
  RiCloseLine, RiArrowRightLine, RiCheckLine,
  RiLightbulbLine, RiTranslate2, RiSettings3Line,
} from 'react-icons/ri';
import { C } from '../../data/constants';
import QuitConfirm from '../../components/shared/QuitConfirm';
import { playMark } from '../../components/brand/celebrate';
import SessionComplete from '../../components/app/SessionComplete';
import StreakCelebration from '../../components/app/StreakCelebration';
import useStreakCelebration from '../../components/app/useStreakCelebration';
import useStudyGuard from '../../components/app/useStudyGuard';

/* ══════════════════════════════════════════════════════════════
   GRAMMAR LESSON RUNNER

   The teaching problem this solves: a beginner shown "これはペンです"
   and asked to translate it has no way in. ペン can be learned like a
   flashcard, but は means nothing on its own — it can only be
   explained by the job it does.

   So nothing is ever tested before it is glossed. Every part runs:

     1. Pattern      the abstract skeleton, A は B です
     2. Break down   the sentence word by word, each word labelled
                     with its reading, its meaning and its role
     3. Translate    rebuild the English from tiles
     4. Build        rebuild the Japanese from tiles
     5. Particle     which particle went here, and why

   Stages 1 and 2 are taught, not graded. Only 3-5 count toward the
   score, and by the time they arrive the learner has already been
   shown every piece.
══════════════════════════════════════════════════════════════ */

/* Roles are colour-coded so the shape of a sentence is visible before
   you can read any of it — every orange chip is a particle. */
const ROLE_COLOR = {
  particle: C.accent,
  copula: C.purple,
  verb: C.blue,
  'i-adjective': C.green,
  'na-adjective': C.green,
  name: C.yellow,
  question: C.red,
};
const roleColor = role => ROLE_COLOR[role] || C.muted;

const ROLE_LABEL = {
  'i-adjective': 'い-adj',
  'na-adjective': 'な-adj',
  pronoun: 'pronoun',
  particle: 'particle',
  copula: 'copula',
};
const roleLabel = role => ROLE_LABEL[role] || role;

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const normalise = s => String(s || '').toLowerCase().replace(/[.,!?;:'"]/g, '').replace(/\s+/g, ' ').trim();

/* ── Build the stage list for a part ─────────────────────────────
   Teach each sentence, then translate it, then — once every sentence
   has been met — the harder production drills. */
function buildStages(part) {
  const sentences = (part.sentences || []).filter(s => s.tokens?.length);
  const stages = [{ kind: 'pattern' }];

  sentences.forEach((sentence, i) => {
    stages.push({ kind: 'gloss', sentence, i });
    if (sentence.drills?.includes('translate') !== false) {
      stages.push({ kind: 'translate', sentence, i });
    }
  });

  sentences.forEach((sentence, i) => {
    if (sentence.drills?.includes('build') !== false) {
      stages.push({ kind: 'build', sentence, i });
    }
  });

  sentences.forEach((sentence, i) => {
    const particles = (sentence.tokens || []).filter(tok => tok.role === 'particle');
    if (particles.length && sentence.drills?.includes('particle') !== false) {
      stages.push({ kind: 'particle', sentence, i, blank: particles[0] });
    }
  });

  return stages;
}

/* ── One annotated word ──────────────────────────────────────── */
function TokenChip({ token, script, open, onToggle }) {
  const colour = roleColor(token.role);
  return (
    <button
      type="button"
      className={`gr-token${open ? ' open' : ''}${token.note ? ' has-note' : ''}`}
      style={{ '--tok': colour }}
      onClick={onToggle}
      title={token.note || undefined}
    >
      <span className="gr-token-jp">{script === 'romaji' ? token.romaji : token.jp}</span>
      <span className="gr-token-gloss">{token.gloss || '—'}</span>
      <span className="gr-token-role">{roleLabel(token.role)}</span>
    </button>
  );
}

/* ── One tile: primary script on top, its counterpart underneath ──
   An English tile carries the Japanese word it came from; a Japanese tile
   carries its romaji. Which of kana/romaji is "primary" follows the
   header toggle, so the learner reads whichever script they are training
   and still sees the other. */
function TileFace({ tile, script }) {
  const jpPrimary = script === 'kana';

  const main = tile.token
    ? (jpPrimary ? tile.token.jp : tile.token.romaji)
    : tile.text;

  const sub = tile.token
    ? (jpPrimary ? tile.token.romaji : tile.token.jp)
    : (jpPrimary ? tile.jp : tile.romaji);

  return (
    <>
      <span className={`gr-tile-main${tile.token && jpPrimary ? ' jp' : ''}`}>{main}</span>
      {sub ? <span className="gr-tile-sub">{sub}</span> : null}
    </>
  );
}

/* ── The sentence, in both scripts ─────────────────────────────
   The toggle picks which script leads; the other always sits underneath.
   Reading わたしも がくせいです is useless to a beginner who cannot yet
   sound it out, and reading only "Watashi mo gakusei desu" never teaches
   the characters — so neither is ever hidden. */
function SentenceLine({ jp, romaji, script, children, sub: subOverride }) {
  const kanaFirst = script === 'kana';
  const main = kanaFirst ? jp : (romaji || jp);
  const sub = subOverride || (kanaFirst ? romaji : jp);

  return (
    <div className="gr-sentence">
      <span className={`gr-sentence-main${kanaFirst ? ' jp' : ''}`}>{children || main}</span>
      {sub ? <span className={`gr-sentence-sub${kanaFirst ? '' : ' jp'}`}>{sub}</span> : null}
    </div>
  );
}

/* ── Tap-to-arrange tiles ──────────────────────────────────────
   Picked tiles fly from the pool slot they were tapped in, and can be
   dragged into a different order once they are in the answer row. */
function TileBoard({ pool, picked, onPick, onUnpick, onReorder, locked, script }) {
  const answerRef = useRef(null);
  const flyFrom = useRef(new Map());          // tile id -> rect it was tapped at
  const [drag, setDrag] = useState(null);     // { id, from, to, dx }

  /* FLIP: the tile is tapped in the pool, then React renders it in the
     answer row. We know both rectangles, so we can play the difference and
     make one move into the other instead of blinking between them. */
  useLayoutEffect(() => {
    if (!flyFrom.current.size || !answerRef.current) return;
    for (const [id, from] of flyFrom.current) {
      const el = answerRef.current.querySelector(`[data-tile="${id}"]`);
      if (!el) continue;
      const to = el.getBoundingClientRect();
      el.animate(
        [
          { transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${Math.max(0.4, from.width / (to.width || 1))})`, opacity: 0.65 },
          { transform: 'none', opacity: 1 },
        ],
        { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
      );
    }
    flyFrom.current.clear();
  }, [picked]);

  const pick = (tile, event) => {
    flyFrom.current.set(tile.id, event.currentTarget.getBoundingClientRect());
    onPick(tile);
  };

  /* ── Drag to reorder ───────────────────────────────────────────
     Pointer events rather than HTML5 drag-and-drop, which does not fire on
     touch. A press that never travels more than a few pixels is still a
     tap, so removing a tile keeps working. */
  const startDrag = (index, event) => {
    if (locked) return;
    const startX = event.clientX;
    const tiles = [...answerRef.current.querySelectorAll('[data-tile]')];
    const centres = tiles.map(t => {
      const r = t.getBoundingClientRect();
      return r.left + r.width / 2;
    });
    let moved = false;
    let target = index;

    const move = ev => {
      const dx = ev.clientX - startX;
      if (!moved && Math.abs(dx) < 5) return;   // still a tap
      moved = true;
      const x = ev.clientX;
      target = centres.reduce((best, c, i) => (Math.abs(x - c) < Math.abs(x - centres[best]) ? i : best), index);
      setDrag({ id: picked[index].id, from: index, to: target, dx });
    };

    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (moved && target !== index) onReorder(index, target);
      else if (!moved) onUnpick(index);
      setDrag(null);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <>
      <div ref={answerRef} className={`gr-answer${picked.length ? '' : ' empty'}`}>
        {picked.length === 0 && <span className="gr-answer-hint">Tap the words below, in order</span>}
        {picked.map((tile, i) => (
          <button
            key={`${tile.id}-${i}`}
            type="button"
            data-tile={tile.id}
            className={`gr-tile picked${drag?.from === i ? ' dragging' : ''}${drag && drag.to === i && drag.from !== i ? ' drop-target' : ''}`}
            disabled={locked}
            style={drag?.from === i ? { transform: `translateX(${drag.dx}px)` } : undefined}
            onPointerDown={e => startDrag(i, e)}
            title="Tap to remove · drag to reorder"
          >
            <TileFace tile={tile} script={script} />
          </button>
        ))}
      </div>

      <div className="gr-pool">
        {pool.map(tile => {
          const used = picked.some(p => p.id === tile.id);
          return (
            <button
              key={tile.id}
              type="button"
              className={`gr-tile${used ? ' used' : ''}`}
              disabled={used || locked}
              onClick={e => pick(tile, e)}
            >
              <TileFace tile={tile} script={script} />
            </button>
          );
        })}
      </div>
    </>
  );
}

export default function GrammarLesson({ chapter, part, onExit, onFinished }) {
  const stages = useMemo(() => buildStages(part), [part]);
  const [step, setStep] = useState(0);
  const [script, setScript] = useState('kana'); // kana | romaji
  const [openToken, setOpenToken] = useState(null);
  const [picked, setPicked] = useState([]);
  const [choice, setChoice] = useState(null);
  const [result, setResult] = useState(null); // 'correct' | 'wrong'
  const [scored, setScored] = useState({ right: 0, total: 0 });
  const [finished, setFinished] = useState(false);
  const { celebration, dismiss: dismissCelebration } = useStreakCelebration(finished);
  useStudyGuard(step > 0 && !finished);
  const [confirmQuit, setConfirmQuit] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef(null);

  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onDown = e => { if (!settingsRef.current?.contains(e.target)) setSettingsOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [settingsOpen]);

  /* Once the result screen is up there is nothing left to lose, so leaving
     from there should not nag. */
  const requestExit = useCallback(() => {
    if (finished || step === 0) onExit();
    else setConfirmQuit(true);
  }, [finished, step, onExit]);

  const stage = stages[step];
  const graded = stage && stage.kind !== 'pattern' && stage.kind !== 'gloss';
  const isLast = step + 1 >= stages.length;

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') requestExit(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestExit]);

  /* ── Tiles for the current stage ─────────────────────────────
     Distractors come from the other sentences in this same part, so a
     wrong tile is always something plausible the learner has met —
     never a random word that can be eliminated on sight. */
  const tiles = useMemo(() => {
    if (!stage) return [];

    if (stage.kind === 'translate') {
      /* Authored English tiles carry the Japanese word they came from. Only
         trust them if they still spell the sentence — otherwise a half-edited
         chapter would silently grade against the wrong answer. */
      const authored = stage.sentence.enTokens || [];
      const usable = authored.length
        && authored.map(t => t.text).join(' ') === stage.sentence.en;

      const words = usable
        ? authored.map(t => ({ text: t.text, jp: t.jp, romaji: t.romaji }))
        : stage.sentence.en.split(/\s+/).filter(Boolean).map(w => ({ text: w }));

      const taken = new Set(words.map(w => w.text));
      /* Also bar any distractor showing Japanese that is already on a real
         tile. さん is glossed "Mr" in one sentence and "Ms" in another, so
         without this the learner was offered two tiles reading さん — one
         correct, one not — with nothing in the Japanese to tell them apart.
         That is a coin toss, not a question. */
      const takenJp = new Set(words.map(w => w.jp).filter(Boolean));
      const otherWords = (part.sentences || [])
        .filter(s => s !== stage.sentence)
        .flatMap(s => (s.enTokens?.length
          ? s.enTokens.map(t => ({ text: t.text, jp: t.jp, romaji: t.romaji }))
          : s.en.split(/\s+/).map(w => ({ text: w }))))
        .filter(w => !taken.has(w.text) && !(w.jp && takenJp.has(w.jp)));

      const seen = new Set();
      const distractors = shuffle(otherWords).filter(w => {
        if (seen.has(w.text)) return false;
        seen.add(w.text);
        return true;
      }).slice(0, 3);

      return shuffle([
        ...words.map((w, i) => ({ id: `w${i}`, ...w })),
        ...distractors.map((w, i) => ({ id: `d${i}`, ...w })),
      ]);
    }

    if (stage.kind === 'build') {
      const toks = stage.sentence.tokens;
      const others = (part.sentences || [])
        .filter(s => s !== stage.sentence)
        .flatMap(s => s.tokens || [])
        .filter(tok => !toks.some(x => x.jp === tok.jp));
      const seen = new Set();
      const distractors = shuffle(others).filter(tok => {
        if (seen.has(tok.jp)) return false;
        seen.add(tok.jp);
        return true;
      }).slice(0, 2);
      return shuffle([
        ...toks.map((tok, i) => ({ id: `t${i}`, token: tok })),
        ...distractors.map((tok, i) => ({ id: `x${i}`, token: tok })),
      ]);
    }

    return [];
  }, [stage, part.sentences]);

  const particleChoices = useMemo(() => {
    if (stage?.kind !== 'particle') return [];
    const pool = (part.sentences || [])
      .flatMap(s => s.tokens || [])
      .filter(tok => tok.role === 'particle' && tok.jp !== stage.blank.jp);
    const seen = new Set();
    const others = pool.filter(tok => {
      if (seen.has(tok.jp)) return false;
      seen.add(tok.jp);
      return true;
    });
    const extras = shuffle(others).slice(0, 3);
    // Always offer a full set even in a part that only uses one particle.
    const fillers = [
      { jp: 'を', romaji: 'o', gloss: '(object marker)' },
      { jp: 'が', romaji: 'ga', gloss: '(subject marker)' },
      { jp: 'に', romaji: 'ni', gloss: '(to / at)' },
    ].filter(f => f.jp !== stage.blank.jp && !extras.some(e => e.jp === f.jp));

    return shuffle([stage.blank, ...[...extras, ...fillers].slice(0, 3)]);
  }, [stage, part.sentences]);

  const reorder = useCallback((from, to) => {
    setPicked(p => {
      const next = [...p];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setPicked([]);
    setChoice(null);
    setResult(null);
    setOpenToken(null);
  }, []);

  const check = useCallback(() => {
    if (!stage || result) return;

    let correct = false;
    if (stage.kind === 'translate') {
      correct = normalise(picked.map(p => p.text).join(' ')) === normalise(stage.sentence.en);
    } else if (stage.kind === 'build') {
      const want = stage.sentence.tokens.map(tok => tok.jp).join('');
      correct = picked.map(p => p.token.jp).join('') === want;
    } else if (stage.kind === 'particle') {
      correct = choice?.jp === stage.blank.jp;
    }

    setResult(correct ? 'correct' : 'wrong');
    playMark(correct ? 'bounce' : 'shake');
    setScored(s => ({ right: s.right + (correct ? 1 : 0), total: s.total + 1 }));
  }, [stage, picked, choice, result]);

  const advance = useCallback(() => {
    if (isLast) { setFinished(true); return; }
    setStep(s => s + 1);
    reset();
  }, [isLast, reset]);

  const retry = useCallback(() => {
    setStep(0);
    setScored({ right: 0, total: 0 });
    setFinished(false);
    reset();
  }, [reset]);

  const scorePct = scored.total ? Math.round((scored.right / scored.total) * 100) : 100;

  useEffect(() => {
    if (finished) onFinished(scorePct);
  }, [finished, scorePct, onFinished]);

  if (!stages.length || !stage) {
    return (
      <div className="gr-runner">
        <div className="gr-runner-head">
          <button className="btn-icon" onClick={onExit}><RiCloseLine size={18} /></button>
        </div>
        <div className="gr-runner-body">
          <p style={{ color: C.muted, textAlign: 'center' }}>
            This part has no sentences yet. Add some in the admin panel.
          </p>
        </div>
      </div>
    );
  }

  /* `step / stages.length` can never reach 100: on the final stage step is
     stages.length - 1, which is why the bar stalled around 90%. Progress is
     "stages behind you", and once the result screen is up every one of them
     is behind you. */
  const progressPct = finished ? 100 : Math.round((step / stages.length) * 100);
  const sentence = stage.sentence;

  return (
    <div className="gr-runner">
      <QuitConfirm
        open={confirmQuit}
        message="You'll lose the drills you've answered in this part."
        onConfirm={onExit}
        onCancel={() => setConfirmQuit(false)}
      />
      <div className="gr-runner-head">
        <button className="btn-icon" onClick={requestExit} title="Back to the chapter (Esc)">
          <RiCloseLine size={18} />
        </button>

        <div className={`gr-runner-bar${progressPct >= 100 ? ' complete' : ''}`}>
          <i style={{ width: `${progressPct}%` }} />
        </div>

        <div className="gr-settings" ref={settingsRef}>
          <button
            type="button"
            className={`gr-settings-btn${settingsOpen ? ' open' : ''}`}
            onClick={() => setSettingsOpen(o => !o)}
            aria-haspopup="menu"
            aria-expanded={settingsOpen}
            title="Lesson settings"
          >
            <RiSettings3Line size={18} />
          </button>

          {settingsOpen && (
            <div className="gr-settings-menu anim-pop" role="menu">
              <span className="gr-settings-label">
                <RiTranslate2 size={13} /> Show Japanese as
              </span>
              <div className="gr-settings-choice">
                {[['kana', 'かな'], ['romaji', 'Romaji']].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    role="menuitemradio"
                    aria-checked={script === key}
                    className={`gr-settings-option${script === key ? ' active' : ''}${key === 'kana' ? ' jp' : ''}`}
                    onClick={() => setScript(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="gr-settings-hint">
                The other script always stays underneath — this only picks which one leads.
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="gr-runner-body">
        {finished ? (
          <SessionComplete
            scorePct={scorePct}
            passed={scorePct >= 80}
            correct={scored.right}
            total={scored.total}
            label="drills correct"
            title={scorePct >= 80 ? 'Part cleared!' : 'Almost there'}
            detail={scorePct >= 80
              ? 'This part is banked. The next one is open.'
              : 'You need 80% to bank this part — the breakdown screens are worth a second read.'}
            onRetry={retry}
            retryLabel="Try again"
            onExit={onExit}
            exitLabel="Back to chapter"
          />
        ) : stage.kind === 'pattern' ? (
          /* ── 1. The skeleton, before any real sentence ── */
          <div className="gr-stage anim-fade-up">
            <span className="gr-stage-kicker">The pattern</span>
            <div className="gr-pattern">{part.pattern || part.title}</div>
            {part.patternEn && <div className="gr-pattern-en">{part.patternEn}</div>}
            {part.explanation && <p className="gr-explain">{part.explanation}</p>}
            {!!part.notes?.length && (
              <ul className="gr-notes">
                {part.notes.map((n, i) => (
                  <li key={i}><RiLightbulbLine size={14} /> <span>{n}</span></li>
                ))}
              </ul>
            )}
            <button className="btn-primary gr-next" onClick={advance}>
              Start <RiArrowRightLine />
            </button>
          </div>
        ) : stage.kind === 'gloss' ? (
          /* ── 2. Word by word: the stage that makes the rest possible ── */
          <div className="gr-stage anim-fade-up">
            <span className="gr-stage-kicker">Break it down</span>
            <SentenceLine jp={sentence.jp} romaji={sentence.romaji} script={script} />

            <div className="gr-tokens">
              {sentence.tokens.map((tok, i) => (
                <TokenChip
                  key={i}
                  token={tok}
                  script={script}
                  open={openToken === i}
                  onToggle={() => setOpenToken(openToken === i ? null : i)}
                />
              ))}
            </div>

            {openToken !== null && sentence.tokens[openToken]?.note && (
              <div className="gr-token-note anim-fade-in">
                <RiLightbulbLine size={14} />
                <span>{sentence.tokens[openToken].note}</span>
              </div>
            )}

            {sentence.literal && (
              <div className="gr-literal">
                <span>Word for word</span>
                <strong>{sentence.literal}</strong>
              </div>
            )}

            <div className="gr-natural">
              <span>In English</span>
              <strong>{sentence.en}</strong>
            </div>

            {sentence.note && <p className="gr-explain small">{sentence.note}</p>}

            <button className="btn-primary gr-next" onClick={advance}>
              Got it <RiArrowRightLine />
            </button>
          </div>
        ) : stage.kind === 'translate' ? (
          /* ── 3. Rebuild the English ── */
          <div className="gr-stage anim-fade-up">
            <span className="gr-stage-kicker">What does it mean?</span>
            <SentenceLine jp={sentence.jp} romaji={sentence.romaji} script={script} />

            <TileBoard
              pool={tiles}
              picked={picked}
              locked={!!result}
              script={script}
              onPick={tile => setPicked(p => [...p, tile])}
              onUnpick={i => setPicked(p => p.filter((_, j) => j !== i))}
              onReorder={reorder}
            />
          </div>
        ) : stage.kind === 'build' ? (
          /* ── 4. Rebuild the Japanese ── */
          <div className="gr-stage anim-fade-up">
            <span className="gr-stage-kicker">Say it in Japanese</span>
            <div className="gr-prompt-en">{sentence.en}</div>

            <TileBoard
              pool={tiles}
              picked={picked}
              locked={!!result}
              script={script}
              onPick={tile => setPicked(p => [...p, tile])}
              onUnpick={i => setPicked(p => p.filter((_, j) => j !== i))}
              onReorder={reorder}
            />
          </div>
        ) : (
          /* ── 5. Which particle, and why ── */
          <div className="gr-stage anim-fade-up">
            <span className="gr-stage-kicker">Which particle?</span>
            <SentenceLine
              script={script}
              jp={sentence.jp}
              romaji={sentence.romaji}
              /* Both lines need the gap in the same place, so each is
                 rebuilt from the tokens rather than shown whole. */
              sub={sentence.tokens.map((tok, i) => (
                <span key={i} className={tok === stage.blank ? 'gr-blank' : undefined}>
                  {tok === stage.blank ? '?' : (script === 'kana' ? `${tok.romaji} ` : tok.jp)}
                </span>
              ))}
            >
              {sentence.tokens.map((tok, i) => (
                <span key={i} className={tok === stage.blank ? 'gr-blank' : undefined}>
                  {tok === stage.blank ? '?' : (script === 'kana' ? tok.jp : `${tok.romaji} `)}
                </span>
              ))}
            </SentenceLine>
            <div className="gr-prompt-en">{sentence.en}</div>

            <div className="gr-choices">
              {particleChoices.map((opt, i) => {
                const selected = choice?.jp === opt.jp;
                const right = opt.jp === stage.blank.jp;
                let cls = 'gr-choice';
                if (result) {
                  if (right) cls += ' right';
                  else if (selected) cls += ' wrong';
                } else if (selected) cls += ' selected';
                return (
                  <button key={i} type="button" className={cls} disabled={!!result} onClick={() => setChoice(opt)}>
                    <strong>{script === 'romaji' ? opt.romaji : opt.jp}</strong>
                    <span>{opt.gloss}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Graded stages get a check/continue footer ── */}
      {!finished && graded && (
        <div className={`gr-footer${result ? ` ${result}` : ''}`}>
          {result && (
            <div className="gr-feedback">
              <strong>
                {result === 'correct' ? <><RiCheckLine size={15} /> Correct</> : <><RiCloseLine size={15} /> Not quite</>}
              </strong>
              <span>
                {stage.kind === 'translate' && sentence.en}
                {stage.kind === 'build' && (script === 'romaji' ? (sentence.romaji || sentence.jp) : sentence.jp)}
                {stage.kind === 'particle' && `${stage.blank.jp} — ${stage.blank.gloss}`}
              </span>
              {result === 'wrong' && stage.kind === 'particle' && stage.blank.note && (
                <em>{stage.blank.note}</em>
              )}
            </div>
          )}

          {result ? (
            <button className="btn-primary gr-next" onClick={advance}>
              {isLast ? 'See result' : 'Continue'} <RiArrowRightLine />
            </button>
          ) : (
            <button
              className="btn-primary gr-next"
              onClick={check}
              disabled={stage.kind === 'particle' ? !choice : picked.length === 0}
            >
              Check <RiCheckLine />
            </button>
          )}
        </div>
      )}

      {celebration && (
        <StreakCelebration
          streak={celebration.streak}
          lastStudied={celebration.lastStudied}
          onContinue={dismissCelebration}
        />
      )}
    </div>
  );
}
